import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu/dropdown-menu";
import { Icon, type IconName } from "../icons/icon";
import { cn } from "../../lib/utils";
import * as React from "react";
import { Slot } from "radix-ui";

const BREADCRUMB_LABEL_MAX_WIDTH = "min(40vw,200px)";
const DEFAULT_TRAILING_VISIBLE_COUNT = 1;

function Breadcrumb({ ref, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav ref={ref} data-slot="breadcrumb" aria-label="breadcrumb" {...props} />
  );
}

function BreadcrumbList({
  className,
  ref,
  ...props
}: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-1.5 text-sm break-words text-muted-foreground sm:gap-2.5",
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({
  className,
  ref,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      ref={ref}
      className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
      {...props}
    />
  );
}

function BreadcrumbLabelBoundary({
  children,
  className,
  style,
  ...props
}: React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1", className)}
      style={{ maxWidth: BREADCRUMB_LABEL_MAX_WIDTH, ...style }}
      {...props}
    >
      {children}
    </span>
  );
}

function BreadcrumbLabel({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      data-slot="breadcrumb-label"
      className={cn("block min-w-0 flex-1 truncate", className)}
      {...props}
    >
      {children}
    </span>
  );
}

function BreadcrumbLink({
  asChild,
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot.Root : "a";
  const shouldWrapLabel =
    !asChild && (typeof children === "string" || typeof children === "number");

  return (
    <Comp
      data-slot="breadcrumb-link"
      ref={ref}
      className={cn(
        "inline-flex min-w-0 items-center gap-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring",
        className,
      )}
      {...props}
    >
      {shouldWrapLabel ? (
        <BreadcrumbLabelBoundary>
          <BreadcrumbLabel>{children}</BreadcrumbLabel>
        </BreadcrumbLabelBoundary>
      ) : (
        children
      )}
    </Comp>
  );
}

function BreadcrumbPage({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<"span">) {
  const shouldWrapLabel =
    typeof children === "string" || typeof children === "number";

  return (
    <span
      data-slot="breadcrumb-page"
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn(
        "pointer-events-none inline-flex min-w-0 items-center gap-1 font-normal text-foreground select-none",
        className,
      )}
      {...props}
    >
      {shouldWrapLabel ? (
        <BreadcrumbLabelBoundary>
          <BreadcrumbLabel>{children}</BreadcrumbLabel>
        </BreadcrumbLabelBoundary>
      ) : (
        children
      )}
    </span>
  );
}

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    data-slot="breadcrumb-separator"
    role="presentation"
    aria-hidden="true"
    className={cn(
      "inline-flex size-[15px] shrink-0 items-center justify-center [&>svg]:size-[15px]",
      className,
    )}
    {...props}
  >
    {children ?? <Icon src="ChevronRight" />}
  </li>
);

const BreadcrumbEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    data-slot="breadcrumb-ellipsis"
    role="presentation"
    aria-hidden="true"
    className={cn("flex size-6 items-center justify-center", className)}
    {...props}
  >
    <Icon src="Ellipsis" className="size-4" />
    <span className="sr-only">More</span>
  </span>
);

export type ResponsiveBreadcrumbItemBase = {
  iconName?: IconName;
  id: string;
  label: React.ReactNode;
};

export type ResponsiveBreadcrumbItemHref = ResponsiveBreadcrumbItemBase & {
  href: string;
};

export type ResponsiveBreadcrumbItemStatic = ResponsiveBreadcrumbItemBase & {
  href?: never;
};

export type ResponsiveBreadcrumbItem =
  | ResponsiveBreadcrumbItemHref
  | ResponsiveBreadcrumbItemStatic;

export type ResponsiveBreadcrumbProps = Omit<
  React.ComponentPropsWithoutRef<"nav">,
  "children"
> & {
  collapsedItemsLabel: string;
  items: readonly ResponsiveBreadcrumbItem[];
  separator?: React.ReactNode;
  trailingVisibleCount?: number;
};

function BreadcrumbItemContent({
  iconName,
  label,
}: Pick<ResponsiveBreadcrumbItem, "iconName" | "label">) {
  return (
    <BreadcrumbLabelBoundary>
      {iconName && <Icon src={iconName} />}
      <BreadcrumbLabel>{label}</BreadcrumbLabel>
    </BreadcrumbLabelBoundary>
  );
}

function isResponsiveBreadcrumbHrefItem(
  item: ResponsiveBreadcrumbItem,
): item is ResponsiveBreadcrumbItemHref {
  return "href" in item && item.href != null;
}

function ResponsiveBreadcrumbStaticItem({
  item,
  className,
}: {
  item: ResponsiveBreadcrumbItemStatic;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1 font-normal text-muted-foreground",
        className,
      )}
    >
      <BreadcrumbItemContent {...item} />
    </span>
  );
}

function ResponsiveBreadcrumbVisibleItem({
  item,
  className,
}: {
  item: ResponsiveBreadcrumbItem;
  className?: string;
}) {
  if (!isResponsiveBreadcrumbHrefItem(item)) {
    return <ResponsiveBreadcrumbStaticItem className={className} item={item} />;
  }

  return (
    <BreadcrumbLink className={className} href={item.href}>
      <BreadcrumbItemContent {...item} />
    </BreadcrumbLink>
  );
}

function ResponsiveBreadcrumbSeparator({
  children,
}: {
  children: React.ReactNode;
}) {
  return <BreadcrumbSeparator>{children}</BreadcrumbSeparator>;
}

function ResponsiveBreadcrumbCollapsedItems({
  collapsedItems,
  collapsedItemsLabel,
}: {
  collapsedItems: readonly ResponsiveBreadcrumbItem[];
  collapsedItemsLabel: string;
}) {
  if (collapsedItems.length === 0) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={collapsedItemsLabel}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring"
        >
          <BreadcrumbEllipsis />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {collapsedItems.map((item) => (
          <ResponsiveBreadcrumbCollapsedMenuItem key={item.id} item={item} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ResponsiveBreadcrumbCollapsedMenuItem({
  item,
}: {
  item: ResponsiveBreadcrumbItem;
}) {
  if (isResponsiveBreadcrumbHrefItem(item)) {
    return (
      <DropdownMenuItem asChild>
        <a className="min-w-0" href={item.href}>
          <BreadcrumbItemContent {...item} />
        </a>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem className="min-w-0" disabled>
      <BreadcrumbItemContent {...item} />
    </DropdownMenuItem>
  );
}

function getVisibleBreadcrumbItems({
  items,
  trailingVisibleCount,
}: {
  items: readonly ResponsiveBreadcrumbItem[];
  trailingVisibleCount: number;
}) {
  if (items.length <= 2) {
    return {
      collapsedItems: [],
      firstItem: items[0],
      trailingItems: items.slice(1),
    };
  }

  const firstItem = items[0];
  const firstTrailingIndex = Math.max(1, items.length - trailingVisibleCount);
  return {
    collapsedItems: items.slice(1, firstTrailingIndex),
    firstItem,
    trailingItems: items.slice(firstTrailingIndex),
  };
}

function ResponsiveBreadcrumb({
  collapsedItemsLabel,
  items,
  separator,
  trailingVisibleCount = DEFAULT_TRAILING_VISIBLE_COUNT,
  ...props
}: ResponsiveBreadcrumbProps) {
  const { collapsedItems, firstItem, trailingItems } =
    getVisibleBreadcrumbItems({
      items,
      trailingVisibleCount,
    });

  if (!firstItem) return null;

  return (
    <Breadcrumb {...props}>
      <BreadcrumbList>
        <BreadcrumbItem>
          {trailingItems.length === 0 && collapsedItems.length === 0 ? (
            <BreadcrumbPage>
              <BreadcrumbItemContent {...firstItem} />
            </BreadcrumbPage>
          ) : (
            <ResponsiveBreadcrumbVisibleItem item={firstItem} />
          )}
        </BreadcrumbItem>
        {(collapsedItems.length > 0 || trailingItems.length > 0) && (
          <ResponsiveBreadcrumbSeparator>
            {separator}
          </ResponsiveBreadcrumbSeparator>
        )}
        {collapsedItems.length > 0 && (
          <>
            <BreadcrumbItem>
              <ResponsiveBreadcrumbCollapsedItems
                collapsedItems={collapsedItems}
                collapsedItemsLabel={collapsedItemsLabel}
              />
            </BreadcrumbItem>
            <ResponsiveBreadcrumbSeparator>
              {separator}
            </ResponsiveBreadcrumbSeparator>
          </>
        )}
        {trailingItems.map((item, index) => {
          const isLastItem = index === trailingItems.length - 1;
          return (
            <React.Fragment key={item.id}>
              <BreadcrumbItem>
                {isLastItem ? (
                  <BreadcrumbPage>
                    <BreadcrumbItemContent {...item} />
                  </BreadcrumbPage>
                ) : (
                  <ResponsiveBreadcrumbVisibleItem item={item} />
                )}
              </BreadcrumbItem>
              {!isLastItem && (
                <ResponsiveBreadcrumbSeparator>
                  {separator}
                </ResponsiveBreadcrumbSeparator>
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  BreadcrumbLabel,
  ResponsiveBreadcrumb,
};
